import * as moment from 'moment';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import * as _ from 'underscore';

import * as CertificationWatermark from '@tableau/certification-watermark';
import { DataQualityAssetType, DataQualityDetailProps, EmbeddableDataQualityStatusChip } from '@tableau/data-catalog-client-components';
import { BodyCell, DataGrid } from '@tableau/data-grid-3-wrapper';
import { SiteUrls } from '@tableau/shared-urls';
import { ActivitySpinnerSize, ActivitySpinnerWidget, DialogPositioningBehavior, DialogType, DialogWidget, HorizontalOriginOption, Overlay, TabStyles, VerticalOriginOption } from '@tableau/widgets';

import { Formatters, Messages } from '../../../codegen/Localize';
import { CommonStyles } from '../../../common/react/CommonStyles';
import { AngularDirective } from '../../../common/react/components/AngularDirective';
import { CellWithCountAndToolTip } from '../../../common/react/components/dataGrid/CellWithCount';
import { FavoriteToggle } from '../../../common/react/components/FavoriteToggle/FavoriteToggle';
import { InterpolatedString } from '../../../common/react/components/InterpolatedString';
import { DeprecatedReactMenu } from '../../../common/react/components/Menu';
import { IReactTranslateService } from '../../../common/react/IReactTranslateService';
import { ContentClickedEventHandler } from '../../../common/telemetryEvents/types/ContentTelemetryTypes';
import { mouseUpFactory } from '../../../common/telemetryEvents/util/EventHandlerFactories';
import { Duration } from '../../../common/utilities/Duration';
import { imgUrl } from '../../../common/utilities/ImgUrl';
import { FlowOverviewErrorsCellComponent, FlowTaskOutputStepsWithIconsCellComponent, FlowTaskStatusWithIconsCellComponent } from '../../../flow/FlowDataGridDefinitions';
import { cssClass, textEllipsis } from '../../../style';
import { AngularContext } from '../../../ts/factories/AngularContextFactory';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { IScheduleFrequencyDescriptionFilter } from '../../../ts/filters/ScheduleFrequencyDescriptionFilter';
import { SizeFilter } from '../../../ts/filters/SizeFilter';
import { L10n } from '../../../ts/L10n/L10n';
import { ExplorableItemType } from '../../../ts/models/ContentType';
import { FlowOutputStepRunType } from '../../../ts/models/FlowOutputStepRunInfo';
import { IContentInfo } from '../../../ts/models/IContentInfo';
import { dataQualityOwnerToTableauUser } from '../../../ts/models/IDataQualityIndicatorInfo';
import { ExtractEncryptionState, IDataSourceInfo } from '../../../ts/models/IDataSourceInfo';
import { ISubscriptionInfo } from '../../../ts/models/ISubscriptionInfo';
import { OrderKeys } from '../../../ts/models/OrderKeys';
import { RunFlowTaskInfo } from '../../../ts/models/RunFlowTaskInfo';
import { DataSources } from '../../../ts/resources/Datasources';
import { ExtractEncryptionMode } from '../../../ts/resources/Sites';
import { DefinedBy, ISchedule, IScheduleFrequency } from '../../../ts/ServerApi/types/ISchedule';
import { telemetryConsumerWrapper } from '../../contexts/TelemetryConsumerWrapper';
import { CardPositionContext, ContentClickedTelemetryContext, ContentInfoContext } from '../../contexts/TelemetryContexts';
import { stringToDataQualityType } from '../../quality/DataQualityType';
import { ActionsMenu } from '../ActionsMenu/ActionsMenu';
import { Hover } from '../Components/Hover';
import { ExplorerStyles } from '../Explorer/ExplorerStyles';
import { SpaceHeaderStyles } from '../Header/SpaceHeaderStyles';
import { TooltipAnchor } from '../Tooltips/TooltipAnchor';
import { ConnectedMetricValuePillWrapper } from './ConnectedMetricValuePill';
import { MetricUpdatedCellFormatter } from './MetricUpdatedCellFormatter';
import { RightAlignedCell, StyledCellFormatter } from './StyledCellWrapper';
import { CommonColumn } from './types/Columns';

// These are (mostly) a direct copy of common/DataGridCells
// Here they just use the classes from data-grid-3-wrapper
export namespace DataGridCells {

  export function PlainTextCell(propertyName: string, testId?: string): React.StatelessComponent<BodyCell.Props> {
    return StyledCellFormatter(
      function PlainTextCell(props: BodyCell.Props) {
        const text = props.row.data[propertyName];
        return <span title={ text } data-tb-test-id={ testId }>{ text }</span>;
      }
    );
  }

  export function PlainTextCellWithTransform(propertyName: string, transform: (value: any) => string,
                                             testId?: string): React.StatelessComponent<BodyCell.Props> {
    return StyledCellFormatter(
      function PlainTextCellWithTransform(props: BodyCell.Props) {
        const text = transform(props.row.data[propertyName]);
        return <span title={ text } data-tb-test-id={ testId }>{ text }</span>;
      }
    );
  }

  export function FixedValueCell(value: string): React.StatelessComponent<any> {
    return RightAlignedCell(
      function FixedValueCell() {
        return <span title={ value }>{ value }</span>;
      }
    );
  }

  //////// Content Type

  export interface ContentTypeCellOptions {
    imageUrlForRow: (row: DataGrid.Row) => string;
    titleForIcon: (row: DataGrid.Row) => string;
    isCertified: (row: DataGrid.Row) => boolean;
    getTooltipProps: (row: DataGrid.Row) => CertificationWatermark.CertificationTooltipProps;
  }

  export const ContentTypeCell = (options: ContentTypeCellOptions): React.StatelessComponent<BodyCell.Props> => {
    return StyledCellFormatter(
      React.createFactory(class extends React.Component<BodyCell.Props, any> {
        public render(): JSX.Element {

          const icon = <img src={options.imageUrlForRow(this.props.row)} className={ExplorerStyles.ContentTypeCellImg} alt={options.titleForIcon(this.props.row)} title={options.titleForIcon(this.props.row)}/>;
          let iconWrapper;

          if (options.isCertified(this.props.row)){
            const tooltipProps = {...options.getTooltipProps(this.props.row), icon: icon};
            iconWrapper = <CertificationWatermark.CertificationTooltipComponent {...tooltipProps} />;
          } else {
            iconWrapper = icon;
          }

          return (
            <div className={ExplorerStyles.ContentTypeCellContainer}>
              {iconWrapper}
            </div>
          );
        }
      })
    );
  };

  //////// Favorite

  export interface IFavoriteCellOptions {
    onAddFavorite: (row: DataGrid.Row) => void;
    onRemoveFavorite: (row: DataGrid.Row) => void;
    favoriteCellTestIdFn: (row: DataGrid.Row) => string;
    isFavoritable?: (row: DataGrid.Row) => boolean;
  }

  export function FavoriteCell(options: IFavoriteCellOptions): React.StatelessComponent<BodyCell.Props> {
    return React.createFactory(class extends React.Component<BodyCell.Props, any> {
      displayName: 'FavoriteCell';

      private boundAddFavorite = () => options.onAddFavorite(this.props.row);
      private boundRemoveFavorite = () => options.onRemoveFavorite(this.props.row);

      public render(): JSX.Element {
        if (_.isFunction(options.isFavoritable) && options.isFavoritable(this.props.row) === false) {
          return <span className="tb-favorite-button-cell" data-tb-test-id="tb-favorite-unavailable" />;
        }

        let index = this.props.row.data.rowIndex;

        return (
          <ContentInfoContext.Provider value={this.props.row.data as IContentInfo}>
            <CardPositionContext.Provider value={index + 1}>
              <span className="tb-favorite-button-cell"
                    data-tb-test-id={ options.favoriteCellTestIdFn(this.props.row) }>
                <FavoriteToggle isFavorite={ this.props.row.data.favorite }
                                onAddFavorite={ this.boundAddFavorite }
                                onRemoveFavorite={ this.boundRemoveFavorite }
                                canHaveKeyboardFocus={ false } />
              </span>
            </CardPositionContext.Provider>
          </ContentInfoContext.Provider>
        );
      }
    });
  }

  //////// Metric

  export function MetricValueCell(): React.StatelessComponent<BodyCell.Props> {
    return StyledCellFormatter(
      function PlainTextCell(props: BodyCell.Props) {
        return <ConnectedMetricValuePillWrapper row={props.row}/>;
      }
    );
  }

  export function MetricUpdatedCell():  React.StatelessComponent<BodyCell.Props> {
    return MetricUpdatedCellFormatter();
  }

  //////// Select

  export interface ICheckboxCellOptions {
    onCheckboxClicked: (row: DataGrid.Row) => void;
    checkboxCellTestIdFn: (row: DataGrid.Row) => string;
    isRowSelected: (row: DataGrid.Row) => boolean;
  }

  export function CheckboxCell(options: ICheckboxCellOptions): React.Factory<BodyCell.Props> {
    return StyledCellFormatter(
      React.createFactory(class extends React.Component<BodyCell.Props, any> {
        displayName: 'CheckboxCell';

        boundHandleClick = this.handleClick.bind(this);
        handleClick(event: React.MouseEvent<HTMLSpanElement>) {
          const props: BodyCell.Props = this.props;
          options.onCheckboxClicked(props.row);
          event.preventDefault();
          event.stopPropagation();
        }
        render() {
          const props: BodyCell.Props = this.props;
          const classes = ['tb-checkbox'];
          if (options.isRowSelected(props.row)) {
            classes.push('tb-checked');
          }

          return (
            <span className="tb-checkbox-cell"
                  onClick={ this.boundHandleClick }
                  data-tb-test-id={ options.checkboxCellTestIdFn(props.row) }>
              <span className={ classes.join(' ') } />
            </span>
          );
        }
      })
      // remove cell padding and adding a little left margin to our first column
    , {paddingRight: 0, paddingLeft: 0, marginLeft: TabStyles.Spacing.DefaultSpace});
  }

  // Link

  export interface ILink {
    text: string;
    column?: CommonColumn;
    title?: string;
    url: string;
    testId?: string;
    style?: any;
    hideTooltips?: boolean;
    iconClassName?: string;
    iconTitle?: string;
    iconUrl?: string;
    // This is here so that we don't have multiple layers of factories which doesn't play nice with data grid 3.
    // The bug we are working around is having to click twice on a link for the link to actually receive the click event.
    displayJustText?: boolean;
  }

  export function getLinkMarkup(link: ILink, row: DataGrid.Row): JSX.Element {
    let index = row.data.rowIndex;
    const textEllipsis = CommonStyles.Layout.TextEllipsis;
    if (link.column && link.column === CommonColumn.Name) {
      return (
        <ContentInfoContext.Provider value={row.data as IContentInfo}>
          <CardPositionContext.Provider value={index + 1}>
            {telemetryConsumerWrapper(ContentClickedTelemetryContext.Consumer, (sendOpenContentTelemetry: ContentClickedEventHandler) =>
              <a href={link.url}
                onMouseUp={mouseUpFactory<HTMLAnchorElement>(sendOpenContentTelemetry)}
                title={link.hideTooltips ? null : (link.title || link.text)}
                style={_.extend({ display: 'block' }, textEllipsis, link.style)}>
                {link.text}
              </a>
            )
            }
          </CardPositionContext.Provider>
        </ContentInfoContext.Provider>
       );
    } else {
      return (
        <a href={link.url}
          title={link.hideTooltips ? null : (link.title || link.text)}
          style={_.extend({ display: 'block' }, textEllipsis, link.style)}>
          {link.text}
        </a>
      );
    }
  }

  export function LinkCell(linkFn: (row: DataGrid.Row) => ILink): React.Factory<BodyCell.Props> {
    return StyledCellFormatter(
      React.createFactory(class extends React.Component<BodyCell.Props, any> {
        textEllipsis = CommonStyles.Layout.TextEllipsis;

        render() {
          const link: ILink = linkFn(this.props.row);
          if (!link) {
            return null;
          }

          let Icon = null;
          if (link.iconClassName || link.iconTitle) {
            Icon = () => <span data-tb-test-id="data-grid-cell-icon"
                               className={ link.iconClassName }
                               title={ link.hideTooltips ? null : link.iconTitle }
                               style={ {flex: 'none'} } />;
          }

          return (
            <span data-tb-test-id={link.testId}
            style={CommonStyles.Layout.FlexBlockLeftAndCenter}>
            { Icon && <Icon /> }
              { link.displayJustText &&
                <span
                  title={link.hideTooltips ? null : (link.title || link.text)}
                  style={_.extend({ display: 'block' }, this.textEllipsis, link.style)}>
                    {link.text}
                </span>
              }
              { !link.displayJustText &&
                DataGridCells.getLinkMarkup(link, this.props.row)
              }
            </span>
          );
        }
      })
    );
  }

  export interface LinkWithCountProps extends BodyCell.Props {
    linkFn: (row: DataGrid.Row) => ILink;
    count?: string;
    getPopoverContent?: (props: BodyCell.Props) => Promise<JSX.Element>;
  }

  export const LinkWithCount = (props: LinkWithCountProps): JSX.Element => {
    const link = props.linkFn(props.row);
    if (!link) {
      return null;
    }

    const textStyles = _.extend({ display: 'block' }, CommonStyles.Layout.TextEllipsis, link.style);

    let icon: JSX.Element | undefined = undefined;

    if (link.iconUrl || link.iconTitle) {
      icon = <img data-tb-test-id="data-grid-cell-icon"
                        style={ ExplorerStyles.connectsToListViewIconStyle }
                        src = { link.iconUrl }
                        title={ link.hideTooltips ? null : link.iconTitle } />;
    }

    const linkEl: JSX.Element = (
      <a href={ link.url }
        title={ link.hideTooltips ? null : (link.title || link.text) }
        style={ textStyles }>
        { link.text }
      </a>
    );

    const getContent = () => props.getPopoverContent(props);
    return (
      <CellWithCountAndToolTip count={props.count} testId={link.testId} getPopoverContent={getContent}>
        { icon }
        { linkEl }
      </CellWithCountAndToolTip>
    );
  };

  const dataGridCellText = cssClass('plainTextCellWithCount', 'text', textEllipsis, {
    display: 'block'
  });

  function CreateCellText(text: string): JSX.Element {
    return (
      <span data-tb-test-id="data-grid-cell-text"
        className={dataGridCellText}
        title={text}>
        {text}
      </span>
    );
  }

  export function PlainTextCellWithCount(text: string,
    getPopoverContent?: (props: BodyCell.Props) => Promise<JSX.Element>,
    count?: string,
    testId?: string): React.StatelessComponent<BodyCell.Props> {

    return function PlainTextCellWithCount(props) {

      const getContent = () => getPopoverContent(props);
      return (
        <CellWithCountAndToolTip count={count} testId={testId} getPopoverContent={getContent}>
          {CreateCellText(text)}
        </CellWithCountAndToolTip>
      );
    };
  }

  export interface ITextWithIcon {
    text: string;
    title?: string;
    textStyle?: React.CSSProperties;
    iconTitle?: string;
    iconUrl: string;
    hideTooltips?: boolean;
  }

  export interface PlainTextCellWithIconAndCountProps extends BodyCell.Props {
    metadataFn: (rowData: any) => ITextWithIcon;
    getPopoverContent?: (props: BodyCell.Props) => Promise<JSX.Element>;
    count?: string;
    testId?: string;
  }

  export const PlainTextCellWithIconAndCount = (props: PlainTextCellWithIconAndCountProps) : JSX.Element => {
    const data = props.metadataFn(props.row.data);
    const textStyles = _.extend({ display: 'block' }, CommonStyles.Layout.TextEllipsis, data.textStyle);

    const Icon = () => <img data-tb-test-id="data-grid-cell-icon"
      style={ExplorerStyles.connectsToListViewIconStyle}
      src={data.iconUrl}
      title={data.hideTooltips ? null : data.iconTitle} />;

    const Text = () => (
      <span data-tb-test-id="data-grid-cell-text"
        style={textStyles}
        title={data.hideTooltips ? null : (data.title || data.text)}>
        {data.text}
      </span>
    );

    const getContent = () => props.getPopoverContent(props);
    return (
      <CellWithCountAndToolTip count={props.count} testId={props.testId} getPopoverContent={getContent}>
        <Icon />
        <Text />
      </CellWithCountAndToolTip>
    );
  };

  export interface IInteractiveIconTooltip {
    text: string;
    title: string;
    iconClass?: string;
    tooltipText?: string;
    tooltipReplacements?: any;
    tooltipLink?: string;
    tooltipLinkText?: string;
  }

  export interface InteractiveIconTooltipState {
    isDialogOpen: boolean;
  }

  export function InteractiveIconTooltipCell(
    getProps: (row: DataGrid.Row) => IInteractiveIconTooltip,
    translate: IReactTranslateService
  ): React.Factory<BodyCell.Props> {
    return React.createFactory(class extends React.Component<BodyCell.Props, InteractiveIconTooltipState> {
      private iconAnchorElement: HTMLElement;

      constructor(props: any) {
        super(props);
        this.state = { isDialogOpen: false };
      }

      private readonly hideDialog = () => {
        this.setState({ isDialogOpen: false });
      }

      private readonly showDialog = () => {
        this.setState({ isDialogOpen: true });
      }

      private readonly setAnchorElement = (el: HTMLElement) => {
        this.iconAnchorElement = el;
      }

      render() {
        const props = getProps(this.props.row);
        const link = {
          LINK: () =>
            <a
              data-tb-test-id={ 'cell-icon-hover-link' }
              className={ 'tb-data-grid-dialog-tooltip-link' }
              href={ props.tooltipLink }
              style={{ textDecoration: 'none', color: TabStyles.Colors.Discrete, opacity: .80 }}
            >
              { props.tooltipLinkText }
            </a>
        };

        return (
          <span data-tb-test-id={ 'plain-text-cell-icon' }>
            <Hover
              groupName="plainTextCellWithLinkInTooltipIcon"
              onEnter={ this.showDialog }
              onLeave={ this.hideDialog }
              leaveOnScroll={ true }
              className={ this.state.isDialogOpen ? props.iconClass + '-active' : props.iconClass }
              enterDelay={ 0 }
              useDocument={ false }
            >
              <div ref={ this.setAnchorElement } >
                { this.state.isDialogOpen && <DialogWidget
                  testId={ 'interactive-tooltip' }
                  type={ DialogType.Uber }
                  showCloseButton={ false }
                  onCloseRequest={ this.hideDialog }
                  positioningBehavior={ DialogPositioningBehavior.Anchored }
                  anchorEl={ this.iconAnchorElement }
                  anchorOrigin={{ horizontal: HorizontalOriginOption.centerX, vertical: VerticalOriginOption.bottom }}
                  stackedOrigin={{ horizontal: HorizontalOriginOption.left, vertical: VerticalOriginOption.top }}
                  isModal={ false }
                  showFooter={ false }
                  dialogWidth={ 228 }
                  dialogStyle={{ marginTop: CommonStyles.Sizes.NormalIconSize }}
                  dialogContentStyle={{ padding: CommonStyles.Sizes.MediumSpace, fontWeight: 'normal' }}
                  dialogBodyStyle={{ maxWidth: '228px' }}
                  title={ props.title }
                >
                  <div style={{ color: CommonStyles.Colors.Black }}>
                    <InterpolatedString
                      baseStringKey={ props.tooltipText }
                      translate={ translate }
                      replacements={{ ...props.tooltipReplacements, ...link }}
                    />
                  </div>
                </DialogWidget>}
              </div>
            </Hover>
            <span data-tb-test-id="data-grid-cell-text">
              { props.text }
            </span>
          </span>
        );
      }
    });
  }

  export interface IInteractiveImageIconTooltip {
    text: string;
    title: string;
    iconUrl: string;
    iconClass?: string;
    iconTitle?: string;
    tooltipText?: string;
    tooltipLink?: string;
    tooltipLinkText?: string;
  }

  export function InteractiveImageIconTooltipCell(
    getProps: (row: DataGrid.Row) => IInteractiveImageIconTooltip
  ): React.Factory<BodyCell.Props> {
    return React.createFactory(class extends React.Component<BodyCell.Props, InteractiveIconTooltipState> {
      private iconAnchorElement: HTMLElement;

      constructor(props: any) {
        super(props);
        this.state = { isDialogOpen: false };
      }

      private readonly hideDialog = () => {
        this.setState({ isDialogOpen: false });
      }

      private readonly showDialog = () => {
        this.setState({ isDialogOpen: true });
      }

      private readonly setAnchorElement = (el: HTMLElement) => {
        this.iconAnchorElement = el;
      }

      render() {
        const props = getProps(this.props.row);
        return (
          <Hover
            groupName="plainTextCellWithLinkInTooltipIcon"
            onEnter={this.showDialog}
            onLeave={this.hideDialog}
            leaveOnScroll={true}
            className={props.iconClass}
            enterDelay={0}
            useDocument={false}
          >
            <div ref={this.setAnchorElement}>
              <span data-tb-test-id={'plain-text-cell-icon'}>
                <img data-tb-test-id="data-grid-cell-icon"
                  style={ExplorerStyles.statusIconStyle}
                  src={props.iconUrl}
                  title={props.iconTitle} />
              </span>
              <span data-tb-test-id="data-grid-cell-text">
                {props.text}
              </span>
              {this.state.isDialogOpen && <DialogWidget
                testId={'interactive-tooltip'}
                type={DialogType.Uber}
                showCloseButton={false}
                onCloseRequest={this.hideDialog}
                positioningBehavior={DialogPositioningBehavior.Anchored}
                anchorEl={this.iconAnchorElement}
                anchorOrigin={{ horizontal: HorizontalOriginOption.centerX, vertical: VerticalOriginOption.centerY }}
                stackedOrigin={{ horizontal: HorizontalOriginOption.left, vertical: VerticalOriginOption.centerY }}
                isModal={false}
                showFooter={false}
                dialogWidth={260}
                dialogContentStyle={{ padding: CommonStyles.Sizes.MediumSpace, fontWeight: 'normal' }}
                title={props.title}
              >
                <>
                  {props.tooltipText}
                  <a
                    target="_blank"
                    data-tb-test-id={'cell-icon-hover-link'}
                    className={'tb-data-grid-dialog-tooltip-link'}
                    href={props.tooltipLink}
                    style={{ textDecoration: 'none' }}
                  >
                    {props.tooltipLinkText}
                  </a>
                </>
              </DialogWidget>}
            </div>
          </Hover>
        );
      }
    });
  }


  // Date time

  interface DateTimeOptions {
    row: DataGrid.Row;
    propertyName: string;
    nullDateTimeLabel?: string;
  }

  export function DateTimeText(options: DateTimeOptions): string {
    const dateTime = options.row.data[options.propertyName];

    if (options.propertyName === OrderKeys.lastAccessedAt) {
      return dateTime ? Formatters.formatLongBestFitRelativeTime(new Date(dateTime)) : (options.nullDateTimeLabel || '');
    }

    return dateTime ? L10n.localizedDateTime(dateTime) : options.nullDateTimeLabel || '';
  }

  function DateTooltip(options: DateTimeOptions): string {
    const dateTime = options.row.data[options.propertyName];

    if (options.propertyName === OrderKeys.lastAccessedAt) {
      return dateTime ? L10n.localizedDateTime(dateTime) : '';
    }

    return '';
  }

  export function DateTimeCell(propertyName: string, nullDateTimeLabel?: string): React.StatelessComponent<BodyCell.Props> {
    return StyledCellFormatter(
      function DateTimeCell(props: BodyCell.Props) {
        const options = {...props, propertyName, nullDateTimeLabel};
        return (
          <span title={DateTooltip(options)}>
            {DateTimeText(options)}
          </span>
          );
      }
    );
  }

  export function OverridableDateTimeCell(dateProperty: string, overrideProperty: string,
                                          overrideLabel?: string, testId?: string): React.StatelessComponent<BodyCell.Props> {
    return StyledCellFormatter(
      function OverridableDateTimeCell(props: BodyCell.Props) {
        const override = props.row.data[overrideProperty];
        const dateTime = props.row.data[dateProperty];
        // show the overrideLabel if the override is true no matter the value of dateTime
        const text = !override && dateTime ? L10n.localizedDateTime(dateTime) : overrideLabel;
        return (
          <span data-tb-test-id={ testId }>{ text }</span>
        );
      }
    );
  }

  export function SubscriptionNextUpdateDescription(): React.StatelessComponent<BodyCell.Props> {
    return StyledCellFormatter(
      function SubscriptionNextUpdateDescriptionCell(props: BodyCell.Props) {
        const row = props.row;
        const subscription = row.data as ISubscriptionInfo;
        if(FeatureFlags.subscriptionsOnExtractRefreshEnabled && subscription.refreshExtractTriggered != null
          && subscription.refreshExtractTriggered) {
          return CreateCellText(Messages.subscriptionOnExtractRefreshNextUpdate());
        } else if(FeatureFlags.suspendSubscriptions) {
          return OverridableDateTimeCell('runNextAt', 'suspended', Messages.disabled())(props);
        } else {
          return DateTimeCell('runNextAt', Messages.disabled())(props);
        }
      }
    );
  }

  // Actions cell

  export interface ActionCellOptions {
    getContentItem: (row: DataGrid.Row) => IContentInfo;
    isRowSelected: (row: DataGrid.Row) => boolean;
  }

  export function ActionsCell(options: ActionCellOptions,
    ContentFormatter: React.ComponentType<BodyCell.Props>,
    cellOverrides: React.CSSProperties = {},
    contentOverrides: React.CSSProperties = {}
  ): React.Factory<BodyCell.Props> {
    return React.createFactory(class extends React.Component<BodyCell.Props, {}> {

      private containerCellStyle: React.CSSProperties = {
        display: 'flex',
        flexGrow: 1,
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'space-between'
      };

      private contentContainerStyle: React.CSSProperties = {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: 'flex',
        flexDirection: 'row',
        flexGrow: 1
      };

      render() {
        const containerCellStyle = { ... this.containerCellStyle, ...cellOverrides };
        const contentContainerStyle = { ... this.contentContainerStyle, ...contentOverrides};
        return (
          <div style={containerCellStyle}>
            <div style={contentContainerStyle}>
              { <ContentFormatter {...this.props} /> }
              <div style={{width: CommonStyles.Sizes.LargeSpace}}/>
            </div>
            <div style={{marginRight: TabStyles.Spacing.MediumSpace}}>
              <ContentInfoContext.Provider value={options.getContentItem(this.props.row)}>
                <CardPositionContext.Provider value={this.props.row.data.rowIndex + 1}>
                  <ActionsMenu
                    targetItems={[ options.getContentItem(this.props.row) ]}
                    disabled={ options.isRowSelected(this.props.row) }
                    useIcon={true}/>
                </CardPositionContext.Provider>
              </ContentInfoContext.Provider>
            </div>
          </div>
        );
      }
    });
  }

  export function ActionsCellRightAligned(options: ActionCellOptions, ContentFormatter: React.ComponentType<BodyCell.Props>): React.Factory<BodyCell.Props> {
    return ActionsCell(options, ContentFormatter, {}, {justifyContent: 'flex-end'});
  }

  // With Actions cell.
  const ActionsCellStyleOverrides: React.CSSProperties = {
    paddingLeft: 0,         // Don't double up on paddingLeft for the originalColumn
    paddingRight: 0,        // To put the actions menu up against the right edge of the column
    overflow: 'visible'     // To let the action menu extend beyond the row
  };

  export function WithActionCell(originalColumn: DataGrid.Column, actionCellOptions: ActionCellOptions, styleOverrides?: React.CSSProperties): DataGrid.Column {
    const actionCellFormatter = ActionsCell(actionCellOptions, originalColumn.cellFormatter);

    return {
      ...originalColumn,
      cellFormatter: StyledCellFormatter(actionCellFormatter, { ...ActionsCellStyleOverrides, ...styleOverrides})
    };
  }

  export function WithActionCellRightAligned(originalColumn: DataGrid.Column, actionCellOptions: ActionCellOptions, styleOverrides?: React.CSSProperties): DataGrid.Column {
    const actionCellFormatter = ActionsCellRightAligned(actionCellOptions, originalColumn.cellFormatter);

    return {
      ...originalColumn,
      cellFormatter: StyledCellFormatter(actionCellFormatter, { ...ActionsCellStyleOverrides, ...styleOverrides})
    };
  }

  export function LocalizedCount(propertyName: string, testId?: string): React.StatelessComponent<BodyCell.Props> {
    return function LocalizedCountCell(props: BodyCell.Props) {
      const count = props.row.data[propertyName];
      const localizedCount = L10n.localizedCount(count);
      return <span data-tb-test-id={ testId }>{ _.isNumber(count) ? localizedCount : '' }</span>;
    };
  }

  export function LocalizedFloat(property: string | ((item: any) => Number), testId?: string) {
    return function LocalizedFloatCell(props: BodyCell.Props) {
      let float;
      if (property instanceof Function) {
        float = property(props.row.data);
      } else {
        float = props.row.data[property];
      }
      const localizedFloat = L10n.localizedFloat(float);
      return <span data-tb-test-id={ testId }>{ localizedFloat ? localizedFloat : '' }</span>;
    };
  }

  export function SizeCell(propertyName: string, testId?: string): React.StatelessComponent<BodyCell.Props> {
    return function SizeCell(props: BodyCell.Props) {
      const sizeInBytes = props.row.data[propertyName];
      return <span data-tb-test-id={ testId }>{ SizeFilter(sizeInBytes) }</span>;
    };
  }

  const AlertCellStyleOverrides: React.CSSProperties = {
    overflow: 'visible'     // To let the action menu extend beyond the row
  };

  // Alert
  export interface IAlert {
    type: string;
    alertTitle: string;
    angularContext: AngularContext;
    testId?: string;
  }

  export function AlertCell(alertFn: (row: DataGrid.Row) => IAlert): React.StatelessComponent<BodyCell.Props> {
    return StyledCellFormatter(React.createFactory(class extends React.Component<BodyCell.Props, {}> {

      private buttonElement: HTMLElement;
      private seButton = (component: any): void => {
        this.buttonElement = ReactDOM.findDOMNode(component) as HTMLElement;
      }

      render() {
        const alert = alertFn(this.props.row);

        const hasAlert = this.props.row.data['hasAlert'];
        if (!hasAlert) {
          return null;
        }

        const id = this.props.row.data['id'];
        const menuPanelDivId = alert.testId || 'alert-panel';

        return DeprecatedReactMenu.element({
          style: {
            position: 'absolute'
          },
          tbTestId: alert.testId,
          menuPanelDivId: menuPanelDivId,
          menuButtonFactory: () => <span ref={this.seButton} className="tb-data-grid-icon tb-icon-alert"
                        data-tb-test-id={ alert.testId }
                        title={ alert.alertTitle } />,
          menuPanelFactory: () => {
            // TODO: rewrite tb:alert-info as a React component
            return (
              <Overlay
                anchorEl={this.buttonElement}
                allowAutoPosition={true}
                anchorOrigin={{
                  horizontal: HorizontalOriginOption.left,
                  vertical: VerticalOriginOption.bottom} }>
                {AngularDirective.element(
                  {
                    angularContext: alert.angularContext,
                    // tslint:disable-next-line
                    template: "<div id=" + menuPanelDivId + " class='tb-popup-menu-content tb-padded-small' style='min-width: 250px; white-space: normal; line-height: normal'>" +
                    "  <tb:alert-info type=\"'" + alert.type + "'\" id=\"'" + id + "'\"></tb:alert-info>" +
                    '</div>'
                  })
                }
              </Overlay>
            );
          }
        });
      }
    }), AlertCellStyleOverrides);
  }

  // Count with Bar

  /**
   * Renders a cell containing a number with a horizontal bar, the width of which is proportional
   * to the number's magnitude. This effectively turns the entire column into a bar graph. The
   * cell's value is returned by dataValueFn, and the bar sizes are normalized to the return value
   * of maxValueFn. For example, if dataValueFn returns 5 and maxValueFn returns 10, the cell will
   * display 5 and the bar will occupy 50% of the column's width. maxValueFn should return the same
   * value for every row in the grid. If the cell value is greater than 0, then the minimum size of the bar
   * will be 2 pixel.
   *
   * @param dataValueFn A function that returns the cell's data value from the row's data object
   * @param maxValueFn A function that returns the maximum value of all the items from the row's data object
   * @param barElementTestId An optional test ID to override the default.
   * @returns {Factory<BodyCell.Props>}
   * @constructor
   */
  export function LocalizedCountWithBar(
    dataValueFn: (data: any) => number,
    maxValueFn: (data: any) => number,
    barElementTestId?: string
  ): React.StatelessComponent<BodyCell.Props> {
    return StyledCellFormatter(
      function LocalizedCountCellWithBar(props: BodyCell.Props) {
        const count = dataValueFn(props.row.data);
        const max = maxValueFn(props.row.data);

        let displayBarLength: number;
        if (!_.isNumber(max) || max <= 0 || !_.isNumber(count) || count <= 0) {
          displayBarLength = 0;
        } else {
          displayBarLength = (count / max);
        }
        const minBarLength = count > 0  && displayBarLength > 0 ? '2px': '0px';
        const barVerticalMargin = TabStyles.Spacing.DefaultSpace;
        const barStyle: React.CSSProperties = {
          marginTop: barVerticalMargin,
          height: ExplorerStyles.LIST_VIEW_ROW_OUTER_HEIGHT - (2 * barVerticalMargin),
          width: (displayBarLength * 100) + '%',
          minWidth: minBarLength,
          backgroundColor: props.isSelected ?
            CommonStyles.Colors.Grey245 : CommonStyles.Colors.Grey218,
        };

        const textContainerStyle: React.CSSProperties = {
          lineHeight: ExplorerStyles.LIST_VIEW_ROW_OUTER_HEIGHT - (2 * barVerticalMargin) + 'px',
          paddingLeft: CommonStyles.Sizes.DefaultSpace,
          verticalAlign: 'top'
        };

        return (
          <div data-tb-test-id={ barElementTestId || 'metrics-cell-bars' } style={ barStyle }>
            <span style={ textContainerStyle }>{ _.isNumber(count) ? L10n.localizedCount(count) : '' }</span>
          </div>
        );
      }
    );
  }

  // Last Refreshed

  export function LastRefreshedCell(isExtractEncryptionEnabled: boolean, siteEaRMode: ExtractEncryptionMode): React.StatelessComponent<BodyCell.Props> {
    return StyledCellFormatter(
      function(props: BodyCell.Props) {
        const datasource = props.row.data as IDataSourceInfo;

        const labelStyle: React.CSSProperties = {
          marginRight: 18,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        };

        function contentText(): string {
          const dateTimeOptions = {...props, propertyName: 'lastRefreshedAt'};
          // &emsp; = unicode 8195
          return `${DataSources.ExtractStatusOrLive(isExtractEncryptionEnabled, datasource.hasExtracts,
            datasource.extractEncryptionState, datasource.extractCreationPending, siteEaRMode)}${String.fromCharCode(8195)}${DateTimeText(dateTimeOptions)}`;
        }

        return (
          <div style={labelStyle} title={contentText()}>
            {
              FeatureFlags.extractMoveToTheWebEnabled && (datasource.extractEncryptionState === ExtractEncryptionState.pendingDecryption ||
               datasource.extractEncryptionState === ExtractEncryptionState.pendingEncryption ||
               (!datasource.hasExtracts && datasource.extractCreationPending))
               ?
              <span key={1} aria-busy="true" className={SpaceHeaderStyles.ExtractStatusSpinner}>
                    <ActivitySpinnerWidget testId="extract-status" spinnerSize={ActivitySpinnerSize.Small} />
              </span>
              : null
            }
            {contentText()}
          </div>
        );
      }
    );
  }

  // Convert an IContentInfo's content type to a DataQualityDetailProps asset type.
  function contentTypeToAssetType(contentType: ExplorableItemType): DataQualityAssetType | null {
    switch (contentType) {
      case 'datasource':
        return 'PublishedDatasource';
      case 'flow':
        return 'Flow';
      case 'dataconnection':
        return 'WebDataConnector';
      case 'database':
        return 'DatabaseServer';
      case 'table':
        return 'DatabaseTable';
      default:
        return null;
    }
  }

  // Create an appropriate URL for a particular content type.
  function urlForContentType(siteUrls: SiteUrls, contentType: ExplorableItemType, id: string):
  string | null {
    switch (contentType) {
      case 'datasource':
        return siteUrls.datasourceUrl(id);
      case 'flow':
        return siteUrls.flowUrl(id);
      case 'database':
        return siteUrls.catalogDatabaseUrl(id);
      case 'table':
        return siteUrls.catalogTableUrl(id);
      default:
        return null;
    }
  }

  export function DataQualityIndicatorCell(siteUrls: SiteUrls, hasDataQualityIndicator: (row: DataGrid.Row) => boolean):
  React.Factory<BodyCell.Props> {
    return React.createFactory(class extends React.Component<BodyCell.Props, any> {
      render() {
        if (!hasDataQualityIndicator(this.props.row)) {
          return null;
        }

        const contentTypeWithDQW = this.props.row.data as IContentInfo;
        const dataQualityIndicator = _.first(contentTypeWithDQW.dataQualityIndicators);

        const details: DataQualityDetailProps[] = [{
            assetName: contentTypeWithDQW.name,
            assetType: contentTypeToAssetType(contentTypeWithDQW.contentType),
            assetUrl: urlForContentType(siteUrls, contentTypeWithDQW.contentType, contentTypeWithDQW.id),
            author: dataQualityOwnerToTableauUser(dataQualityIndicator.owner),
            localizedDate: L10n.localizedDateTime(dataQualityIndicator.updatedAt),
            type: stringToDataQualityType(dataQualityIndicator.type),
            message: dataQualityIndicator.message,
            onContentItem: true,
            newTab: false
          }];

        return (
            <span className="tb-quality-indicator-cell">
              <EmbeddableDataQualityStatusChip details={details} iconOnly={true} siteUrls={siteUrls} />
            </span>
          );
      }
    });
  }

  // CertificationMarkTooltipCell

  export function CertificationMarkTooltipCell(
    isCertified: (row: DataGrid.Row) => boolean,
    getTooltipProps: (row: DataGrid.Row) => CertificationWatermark.CertificationTooltipProps
  ): React.Factory<BodyCell.Props> {
    return React.createFactory(class extends React.Component<BodyCell.Props, any> {
      displayName: 'CertificationMarkTooltipCell';
      render() {
        if (isCertified(this.props.row)) {
          const tooltipProps = getTooltipProps(this.props.row);
          return <CertificationWatermark.CertificationTooltipComponent {...tooltipProps} />;
        } else {
          return <span></span>;
        }
      }
    });
  }

  export function ScheduleFrequencyDescriptionCell(
    scheduleFrequencyDescriptionFilter: IScheduleFrequencyDescriptionFilter,
  ): React.Factory<BodyCell.Props> {
    return StyledCellFormatter(
      function ScheduleFrequencyDescriptionCell(props: BodyCell.Props) {

        const row = props.row;
        const scheduleFrequency = row.data as IScheduleFrequency;
        const schedule: ISchedule = {
          id: '',
          name: '',
          scheduledAction: '',
          priority: 0,
          taskCount: 0,
          parallel: false,
          serverTimezoneOffset: 0,
          definedBy: DefinedBy.SELF_SERVICE,
          scheduleType: scheduleFrequency.scheduleType,
          scheduleDetails: scheduleFrequency.scheduleDetails
        };

        const scheduleDescription = scheduleFrequencyDescriptionFilter(schedule);

        return (
          <span title={ scheduleDescription }>{ scheduleDescription }</span>
        );
      }
    );
  }

  export function ScheduleDescriptionCellWithIcon(
    subscriptionConditionFn: (row: DataGrid.Row) => boolean,
    translate: IReactTranslateService,
    scheduleUrlFn: ((row: DataGrid.Row) => string) | null,
    scheduleFrequencyDescriptionFilter: IScheduleFrequencyDescriptionFilter,
    subscriptionOnExtractRefreshFn: (row: DataGrid.Row) => boolean
  ): React.Factory<BodyCell.Props> {
    return StyledCellFormatter(
      function ScheduleDescriptionCell(props: BodyCell.Props) {

        const row = props.row;
        const schedule = row.data.schedule as ISchedule;

        let cellStyle = {};
        let textStyles = {};
        let Icon: () => JSX.Element | undefined = undefined;

        if (subscriptionConditionFn(row)) {
          cellStyle = _.extend({ cursor: 'default' }, CommonStyles.Layout.FlexBlockLeftAndCenter);
          textStyles = _.extend({ display: 'block', flexShrink: 1}, CommonStyles.Layout.TextEllipsis);

          const iconStyle = {
            backgroundImage: imgUrl('help_header_icon_dark.svg'),
            height: '35px',
            width: '35px',
            backgroundPosition: 'left center',
            backgroundRepeat: 'no-repeat',
            marginLeft: '5px',
            flexShrink: 0
          };

          Icon = () => <span data-tb-test-id="data-grid-cell-icon"
                             style =  { iconStyle }
                             className= { 'data-present-class' }
                             title={ Messages.column_subscriptionDataPresent() } />;
        }

        if(subscriptionOnExtractRefreshFn(row)) {
          return (
            <span style={cellStyle}>
            <span style={textStyles}>
              <span title={Messages.subscriptionOnExtractRefreshSchedule()}>{Messages.subscriptionOnExtractRefreshSchedule()}</span>
            </span>
            {Icon && <Icon />}
          </span>
          );
        } else {
          const scheduleDescription = scheduleFrequencyDescriptionFilter(schedule);

          const ScheduleCellContents = () => (
            <span>
              {scheduleUrlFn
                ? <a href={scheduleUrlFn(row)} title={schedule.name}>{schedule.name}</a>
                : <span title={schedule.name}>{schedule.name}</span>
              }
            </span>
          );

          const Dash = () => <span>&nbsp;&ndash;&nbsp;</span>;

          const selfService = schedule.definedBy === DefinedBy.SELF_SERVICE;
          //Exclude the dash and the schedule link if the schedule was created by self service

          return (
            <span style={ cellStyle }>
              <span style={ textStyles }>
                  {selfService ? null : ScheduleCellContents()}
                {selfService ? null : Dash()}
                <span title={ scheduleDescription }>{ scheduleDescription }</span>
              </span>
              { Icon && <Icon /> }
            </span>
          );
        }
      }
    );
  }

  export function RunTypeCell(): React.StatelessComponent<BodyCell.Props> {
    return DataGridCells.PlainTextCellWithTransform('runType',
      (runType: FlowOutputStepRunType) => {
        return runType === FlowOutputStepRunType.scheduled ? Messages.flowRunHistoryColumn_runTypeScheduled() : Messages.flowRunHistoryColumn_runTypeOndemand();
      }
    );
  }

  export function DurationCell(
    startTimePropertyName: string,
    endTimePropertyName: string,
    nullDateTimeLabel?: string,
    testId?: string): React.StatelessComponent<BodyCell.Props> {
      return StyledCellFormatter(
        function DurationCell(props) {
          const startDateTime = props.row.data[startTimePropertyName];
          const endDateTime = props.row.data[endTimePropertyName];
          const duration = new Duration(startDateTime, endDateTime);
          const minimumTwoDigits = Intl.NumberFormat('en-US', { minimumIntegerDigits: 2 });
          if (startDateTime == null || endDateTime == null || duration.isNegative) {
            return nullDateTimeLabel == null ? null : <span data-tb-test-id={ testId }>{ nullDateTimeLabel }</span>;
          }
          return (
            <TooltipAnchor
              width={ 250 }
              delay={ 0 }
              title={Messages.flowRunHistoryColumn_duration()}
              tooltipContent={() => {
                  return (
                    <div>
                      <a href=""/>
                      <div className={'tb-content-item-popover-title'}>
                        { Messages.flowRunHistoryColumn_duration_title() }
                      </div>
                      <div className={'tb-content-item-popover-description'}>
                        { duration.hours > 0 ?
                          Messages.flowRunHistoryColumn_duration_message_hoursMinutesSeconds({
                            HOURS_COUNT: duration.hours,
                            HOURS_LOC: Formatters.formatCount(duration.hours),
                            MINUTES_COUNT: duration.minutes,
                            MINUTES_LOC: Formatters.formatCount(duration.minutes),
                            SECONDS_COUNT: duration.seconds,
                            SECONDS_LOC: Formatters.formatCount(duration.seconds),
                          }) : duration.minutes > 0 ?
                          Messages.flowRunHistoryColumn_duration_message_minutesSeconds({
                            MINUTES_COUNT: duration.minutes,
                            MINUTES_LOC: Formatters.formatCount(duration.minutes),
                            SECONDS_COUNT: duration.seconds,
                            SECONDS_LOC: Formatters.formatCount(duration.seconds),
                          }) :
                          Messages.flowRunHistoryColumn_duration_message_seconds({
                            SECONDS_COUNT: duration.seconds,
                            SECONDS_LOC: Formatters.formatCount(duration.seconds),
                          })
                        }
                      </div>
                    </div>
                  );
                }
              }
            >
              <div data-tb-test-id={ testId } style={CommonStyles.Layout.TextEllipsis}>
                {
                  minimumTwoDigits.format(duration.hours) + ':' +
                  minimumTwoDigits.format(duration.minutes) + ':' +
                  minimumTwoDigits.format(duration.seconds)
                }
              </div>
            </TooltipAnchor>
          );
        }
      );
  }

  export function FlowTaskOutputStepsWithIconsCell(): React.StatelessComponent<BodyCell.Props> {
    return StyledCellFormatter(
      function FlowTaskOutputStepsWithIconsCell(props: BodyCell.Props) {
        const data = props.row.data;
        return (
          <FlowTaskOutputStepsWithIconsCellComponent flowId={data.flowId} outputSteps={data.flowOutputSteps}/>
        );
      }
    );
  }

  export function FlowTaskStatusWithIconsCell(siteUrls: SiteUrls, flowFailureThresholdForRunPrevention: number): React.StatelessComponent<BodyCell.Props> {
    return StyledCellFormatter(
      function FlowTaskStatusWithIconsCell(props: BodyCell.Props) {
        const data = props.row.data;
        return (
          <FlowTaskStatusWithIconsCellComponent
            flowId={data.flowId}
            siteUrls={siteUrls}
            runFlowTaskInfo={data as RunFlowTaskInfo}
            flowFailureThresholdForRunPrevention={flowFailureThresholdForRunPrevention}
          />
        );
      }
    );
  }

  export function FlowRunHistoryErrorsCell(siteUrls: SiteUrls, isDownloadAllowed: boolean): React.StatelessComponent<BodyCell.Props> {
    return StyledCellFormatter(
      function FlowRunHistoryErrorsCell(props: BodyCell.Props) {
        const data = props.row.data;
        return (
          <FlowOverviewErrorsCellComponent
            errors={data.flowRunErrors}
            flowId={data.flowId}
            onConnectionsPage={false}
            siteUrls={siteUrls}
            isDownloadAllowed={isDownloadAllowed}
          />
        );
      }
    );
  }

  export interface ITextWithIcon2 {
    text: string;
    title?: string;
    textStyle?: React.CSSProperties;
    iconClass: string;
    iconTitle?: string;
    hideTooltips?: boolean;
  }

  export function PlainTextCellWithIcon(metadataFn: (rowData: any) => ITextWithIcon2,
                                        testId?: string): React.StatelessComponent<BodyCell.Props> {
    return StyledCellFormatter(
      function PlainTextCellWithIcon(props) {
        const data = metadataFn(props.row.data);

        return (
          <span data-tb-test-id={ testId || 'plain-text-cell-icon' }>
            <span data-tb-test-id="data-grid-cell-icon"
                  className={ data.iconClass }
                  title={ data.hideTooltips ? null : data.iconTitle } />
            <span data-tb-test-id="data-grid-cell-text"
                  title={ data.hideTooltips ? null : (data.title || data.text) }>
              { data.text }
            </span>
          </span>
        );
      }
    );
  }

  export function extractTaskStatus() {
    return StyledCellFormatter(
      function extractTaskStatus(props: BodyCell.Props) {
        const state = props.row.data.state;
        return <span>{ state }</span>;
      }
    );
  }

}
